import {BaseModel} from '@shared/models/base.model';
import {
  CompanyCapitalType, CompanyRegisteredAddresStatusEnum,
  CompanyRegisteredAddressType,
  CompanyStatusEnum,
  CompanyType,
  GstStatusEnum,
  ItemsToVerifyType,
  KycStatusEnum,
} from '@generated/graphql';
import {Country} from '@modules/country/models/country.model';
import {User} from '@modules/user/models/user.model';
import {countryFactory} from '@modules/country/factories/country.factory';
import {userFactory} from '@modules/user/factories/user.factory';
import {CompanySecretary} from '@modules/company/models/company-secretary.model';
import {companySecretaryFactory} from '@modules/company/factories/company-secretary.factory';
import {CompanyAuditor} from '@modules/company/models/company-auditor.model';
import {companyAuditorFactory} from '@modules/company/factories/company-auditor.factory';
import {companyShareholderFactory} from '@modules/company/factories/company-shareholder.factory';
import {CompanyShareholder} from '@modules/company/models/company-shareholder.model';
import {CompanyDirector} from "@modules/company/models/company-director.model";
import {companyDirectorFactory} from "@modules/company/factories/company-director.factory";
import {MissingKycDocument} from "@modules/officer/models/missing-kyc-document.model";
import {missingKycDocumentFactory} from "@modules/officer/factories/missing-kyc-document.factory";
import {snakeCase} from "lodash-es";
import {CompanyIndirectShareholder} from "@modules/company/models/company-indirect-shareholder.model";
import {Corporate} from "@modules/company/models/corporate.model";
import {corporateFactory} from "@modules/company/factories/corporate.factory";
import {DateTime} from "luxon";
import {Job} from "@modules/job/models/job.model";
import {jobFactory} from "@modules/job/factories/job.factory";
import {GstReturn} from "@modules/gst-return/models/gst-return.model";
import {gstReturnFactory} from "@modules/gst-return/factories/gst-return.factory";
import {companyGstReturnFactory} from "@modules/company/factories/company-gst-return.factory";
import {CompanyGstReturn} from "@modules/company/models/company-gst-return.model";
import {
  MissingCompanyStakeholderKycDocument
} from "@modules/company/models/missing-company-stakeholder-kyc-document.model";
import {
  missingCompanyStakeholderKycDocumentFactory
} from "@modules/company/factories/missing-company-stakeholder-kyc-document.factory";
import { ItemsToVerify } from './items-to-verify.model';
import { itemsToVerifyFactory } from '../factories/items-to-verify.factory';
import { File } from '@modules/file/models/file.model';
import { fileFactory } from '@modules/file/factories/file.factory';

export class Company extends BaseModel implements CompanyType {
  id: string;
  kycStatus: KycStatusEnum;
  gstStatus: GstStatusEnum;
  fyeDate: Date;
  registrationDate?: Date;
  registrationNumber?: string;
  name: string;
  status: CompanyStatusEnum;
  country: Country;
  createdBy: User;
  owner?: User;
  users: User[];
  companyActivity?: string;
  companyCapitals?: CompanyCapitalType[];
  createdById: string;
  directors?: CompanyDirector[];
  numberOfEmployees?: number;
  registeredAddresses?: CompanyRegisteredAddressType[];
  shareholders?: CompanyShareholder[];
  companySecretaries: CompanySecretary[];
  companyAuditors: CompanyAuditor[];
  canCreateCompanyAuditor: boolean;
  canCreateCompanySecretary: boolean;
  canConfirmKyc: boolean;
  canSubmitKyc: boolean;
  website?: string;
  secondName?: string;
  thirdName?: string;
  missingKycDocuments?: MissingKycDocument[]
  shareholdersForOrganizationChart?: Corporate[]
  newChangeFYEDateJob?: Job
  newGstRegistrationJob?: Job
  gstReturns?: GstReturn[]
  companyGstReturn?: CompanyGstReturn
  companyGstReturnForOwner?: CompanyGstReturn
  experts?: User[]
  customers?: User[]
  newGstReturnJobs?: Job[]
  rootDocumentsPath: string
  missingCompanyStakeholderKycDocuments: MissingCompanyStakeholderKycDocument[]
  missingCompanyStakeholderKycDocumentTotal: number
  itemsToVerify: ItemsToVerify;
  specialLicenseDocument?: File;
  _model: string

  get companyName(): string {
    return this.name;
  }

  get countryName(): string {
    return this.country.name;
  }

  get organizationChartFileName(): string {
    return snakeCase(`${this.name}_organization_chart`);
  }

  get isGstRegistered() {
    return this.gstStatus === GstStatusEnum.Registered
  }

  constructor(state: CompanyType) {
    super(state);

    this.country = countryFactory.tryCreating(state.country);

    this.createdBy = userFactory.tryCreating(state.createdBy);

    this.owner = userFactory.tryCreating(state.owner);

    this.users = userFactory.createEntities(state.users);

    this.companySecretaries = companySecretaryFactory.createEntities(
      state.companySecretaries,
    );

    this.companyAuditors = companyAuditorFactory.createEntities(
      state.companyAuditors,
    );

    this.shareholders = companyShareholderFactory.createEntities(
      state.shareholders,
    );

    this.directors = companyDirectorFactory.createEntities(state.directors);

    this.missingKycDocuments = missingKycDocumentFactory.createEntities(state.missingKycDocuments)

    const organizationChartShareholders = state.shareholdersForOrganizationChart
      ? corporateFactory.createEntities(state.shareholdersForOrganizationChart)
      : []

    this.shareholdersForOrganizationChart = organizationChartShareholders.length
      ? organizationChartShareholders
      : this.flattenCorporates(this.shareholders)

    this.hydrateOrganizationChartRelations()

    this.newChangeFYEDateJob = jobFactory.tryCreating(state.newChangeFYEDateJob)

    this.newGstRegistrationJob = jobFactory.tryCreating(state.newGstRegistrationJob)

    this.gstReturns = gstReturnFactory.createEntities(state.gstReturns)

    this.companyGstReturn = companyGstReturnFactory.tryCreating(state.companyGstReturn)

    this.companyGstReturnForOwner = companyGstReturnFactory.tryCreating(state.companyGstReturnForOwner)

    this.experts = userFactory.createEntities(state.experts)

    this.customers = userFactory.createEntities(state.customers)

    this.missingCompanyStakeholderKycDocumentTotal = state.missingCompanyStakeholderKycDocumentTotal ?? 0

    this.missingCompanyStakeholderKycDocuments = missingCompanyStakeholderKycDocumentFactory.createEntities(state.missingCompanyStakeholderKycDocuments)

    this.itemsToVerify = itemsToVerifyFactory.tryCreating(state.itemsToVerify)
      ?? itemsToVerifyFactory.create({
        total: 0,
        items: [],
        allVerified: false,
      } as ItemsToVerifyType)

    this.specialLicenseDocument = fileFactory.tryCreating(
      state.specialLicenseDocument,
    )
  }

  private hydrateOrganizationChartRelations(): void {
    const indirectShareholders = this.organizationChartIndirectShareholders

    if (!indirectShareholders.length) {
      return
    }

    // `shareholders` only contains direct shareholders from the company payload.
    // Org-chart navigation should always resolve nodes from `shareholdersForOrganizationChart`,
    // which contains the full flattened graph, then hydrate parent/child relations onto those nodes.
    const organizationChartCorporatesById = new Map<string, Corporate>();

    this.organizationChartCorporates.forEach((shareholder) => {
      organizationChartCorporatesById.set(shareholder.id, shareholder);
    })

    const shareholdersByParentId = indirectShareholders.reduce(
      (map, shareholder) => {
        const parentId = shareholder.companyId ?? shareholder.company?.id

        if (!parentId) {
          return map
        }

        shareholder.company = organizationChartCorporatesById.get(parentId) ?? shareholder.company

        const items = map.get(parentId) ?? []
        items.push(shareholder)
        map.set(parentId, items)

        return map
      },
      new Map<string, CompanyIndirectShareholder[]>(),
    )

    this.organizationChartCorporates.forEach((shareholder) => {
      shareholder.shareholders = shareholdersByParentId.get(shareholder.id) ?? []
    })

    // Keep the direct-shareholder list in sync for consumers that still read nested data from it.
    this.shareholders.forEach((shareholder) => {
      shareholder.shareholders = shareholdersByParentId.get(shareholder.id) ?? []
    })

    indirectShareholders.forEach((shareholder) => {
      shareholder.shareholders = shareholdersByParentId.get(shareholder.id) ?? []
    })
  }

  get corporates(): Corporate[] {
    return this.flattenCorporates(this.organizationChartCorporates)
  }

  get organizationChartIndirectShareholders(): CompanyIndirectShareholder[] {
    return this.getIndirectShareholdersFromCorporates(
      this.organizationChartCorporates,
    )
  }

  findOrganizationChartIndirectShareholder(
    id: string,
  ): CompanyIndirectShareholder | undefined {
    return this.organizationChartIndirectShareholders.find(
      (shareholder) => shareholder.id === id,
    )
  }

  findOrganizationChartCorporate(id: string): Corporate | undefined {
    return this.organizationChartCorporates.find((corporate) => corporate.id === id)
  }

  isOrganizationChartIndirectShareholder(id: string): boolean {
    return Boolean(this.findOrganizationChartIndirectShareholder(id))
  }

  get organizationChartCorporates(): Corporate[] {
    return this.shareholdersForOrganizationChart ?? []
  }

  buildOrganizationChartPath(targetId: string): string[] {
    const path: string[] = []
    let currentCorporate = this.findOrganizationChartCorporate(targetId)

    while (currentCorporate?.id && currentCorporate.id !== this.id) {
      path.unshift(currentCorporate.id)

      if (currentCorporate.companyId === this.id) {
        break
      }

      currentCorporate = this.findOrganizationChartCorporate(currentCorporate.companyId)
    }

    return path
  }

  getModelName(): string {
    return Company.name;
  }

  private getIndirectShareholdersFromCorporates(
    corporates: Corporate[],
  ): CompanyIndirectShareholder[] {
    return (corporates ?? []).filter(
      (corporate): corporate is CompanyIndirectShareholder =>
        corporate.isIndirect(),
    )
  }

  private flattenCorporates(corporates: Corporate[]): Corporate[] {
    const flatten = (item: Corporate): Corporate[] => {
      const shareholders = item.shareholders ?? []

      return [
        item,
        ...shareholders.flatMap((shareholder) => flatten(shareholder)),
      ]
    }

    return (corporates ?? []).flatMap((corporate) => flatten(corporate))
  }

  public getNextFYEValue(): Date {
    const currentFYEDate = DateTime.fromFormat(
      this.fyeDate.toString(),
      'yyyy-MM-dd HH:mm:ss',
    );

    const now = DateTime.now();

    const incorporationDate = this.registrationDate
      ? DateTime.fromFormat(
        this.registrationDate.toString(),
        'yyyy-MM-dd HH:mm:ss',
      )
      : null;

    const result = currentFYEDate;

    if (incorporationDate) {
      if (now.year < incorporationDate.year) {
        result.set({year: incorporationDate.year});
      } else {
        result.set({year: now.year});
      }
    } else {
      result.set({year: now.year});
    }

    return result.toJSDate();
  }


  companyPath(): string {
    return `${window.origin}/companies/${this.id}`;
  }

  // TODO hardcoded
  get isECISupported(): boolean {
    return this.country.isSingapore
  }

  // TODO hardcoded
  get isFormCSupported(): boolean {
    return this.country.isSingapore
  }

  // TODO hardcoded
  get isProfitsTaxReturnSupported(): boolean {
    return this.country.isHongKong
  }

  get registeredAddress () {
    const data = (this.registeredAddresses ?? []).filter(
      a => a.status === CompanyRegisteredAddresStatusEnum.Active
    )

    return data.length ? data[0] : null
  }

  get registeredAddressToString () {
    if (this.registeredAddress) {
      const {apartment, address, country, city, state, zip} = this.registeredAddress.address

      return [apartment, address, city, state, country?.name, zip]
        .filter(Boolean)
        .join(', ');
    }

    return ''
  }

  get corporateShareholders () {
    return this.shareholders.filter(sh => sh.officer.isCorporate)
  }

  get individualShareholders () {
    return this.shareholders.filter(sh => sh.officer.isIndividual)
  }
}
